import { type ReactNode, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useServices } from '@/app/services';
import { useFolders, useMediaQuery, useNotes } from '@/hooks/hooks';
import { useNoteActions } from '@/hooks/useNoteActions';
import { Icon, type IconName } from '@/ui/Icon';
import { useSnackbar } from '@/ui/snackbar';
import { FolderTree } from './FolderTree';
import { SyncIndicator } from './SyncIndicator';

export interface ShellContext {
  openNav: () => void;
  isWide: boolean;
}

function NavItem({ to, icon, label, onClick, end }: { to: string; icon: IconName; label: string; onClick?: () => void; end?: boolean }) {
  return (
    <NavLink to={to} end={end} className="nav-item" onClick={onClick}>
      <Icon name={icon} />
      <span className="nav-item__label">{label}</span>
    </NavLink>
  );
}

function NavContent({ onNavigate }: { onNavigate?: () => void }) {
  const notes = useNotes();
  const { tree } = useFolders(notes);
  const { create } = useNoteActions();
  const navigate = useNavigate();
  const newNote = async () => {
    const n = await create('text');
    onNavigate?.();
    void navigate(`/note/${n.id}`);
  };
  return (
    <nav className="nav" aria-label="Hauptnavigation">
      <div className="nav__header">
        <span className="nav__brand">
          <Icon name="tag" size={28} /> tagn
        </span>
        <SyncIndicator />
      </div>
      <button type="button" className="nav__compose" onClick={() => void newNote()}>
        <Icon name="edit_note" />
        Neue Notiz
      </button>
      <NavItem to="/" end icon="lightbulb" label="Notizen" onClick={onNavigate} />
      <NavItem to="/shared" icon="group" label="Geteilt" onClick={onNavigate} />
      <NavItem to="/archive" icon="archive" label="Archiv" onClick={onNavigate} />
      <h2 className="nav__section">Ordner</h2>
      {tree.length === 0 ? (
        <p className="nav__empty">Schreibe #Tag in eine Notiz, um Ordner anzulegen.</p>
      ) : (
        <FolderTree folders={tree} onNavigate={onNavigate} />
      )}
      <div className="nav__spacer" />
      <NavItem to="/settings" icon="settings" label="Einstellungen" onClick={onNavigate} />
    </nav>
  );
}

/** Layout for signed in users: navigation drawer (rail on desktop) + content. */
export function AppShell({ children }: { children?: ReactNode }) {
  const isWide = useMediaQuery('(min-width: 840px)');
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const [path, setPath] = useState(location.pathname);
  if (path !== location.pathname) {
    // Adjust state while rendering (React docs: "storing information from previous renders").
    setPath(location.pathname);
    setNavOpen(false);
  }
  const { sync } = useServices();
  const snackbar = useSnackbar();

  useEffect(() => sync.start(), [sync]);
  useEffect(() => {
    let conflicts = sync.getState().conflicts;
    return sync.subscribe((s) => {
      if (s.conflicts > conflicts) snackbar({ text: 'Konflikt: Deine Version wurde als Kopie gespeichert.', duration: 6000 });
      conflicts = s.conflicts;
    });
  }, [sync, snackbar]);

  const context: ShellContext = { openNav: () => setNavOpen(true), isWide };
  return (
    <div className="shell">
      {isWide ? (
        <aside className="shell__rail">
          <NavContent />
        </aside>
      ) : (
        <div className={`drawer${navOpen ? ' is-open' : ''}`} aria-hidden={!navOpen}>
          <div className="drawer__scrim" onClick={() => setNavOpen(false)} />
          <aside className="drawer__sheet">{navOpen && <NavContent onNavigate={() => setNavOpen(false)} />}</aside>
        </div>
      )}
      <main className="shell__main">{children ?? <Outlet context={context} />}</main>
    </div>
  );
}
